import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { styled } from '@mui/material/styles'
import { IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { colorPalette } from '../../style/colorPalette'
import { AllState } from '../../../store'
import { AiAgentWbsItemRef } from '../../../lib/functions/ai'
import { WbsItemType } from '../../../domain/entity/WbsItemEntity'
import { WbsItemIcon } from '../../components/icons/WbsItemIcon'

// カンバンのカード（components/kanban/items/Card.tsx）と同じ、白地に細い枠の見た目に揃える。
const ChipRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'removable',
})<{ removable: boolean }>(({ removable }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: 220,
  minWidth: 0,
  overflow: 'hidden',
  // 削除ボタンがあるときは、ボタン自身の内側の余白が右端の余白として効くため詰める。
  // ボタンが無い送信済みバブル側では、名称が枠に接して見えるので左右を揃える。
  padding: removable ? '2px 2px 2px 6px' : '2px 6px',
  borderRadius: 4,
  border: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: colorPalette.monotone[0],
  color: colorPalette.monotone[10],
  fontSize: 12,
  fontWeight: 600,
}))

const TypeIcon = styled(WbsItemIcon)({
  width: 16,
  height: 16,
  flexShrink: 0,
  display: 'block',
})

// minWidth: 0 が無いと flex アイテムの既定 min-width: auto でテキストの全長まで
// しか縮まず、maxWidth を超えた名称が枠からはみ出す。
const Label = styled('span')({
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const RemoveButton = styled(IconButton)({
  width: 18,
  height: 18,
  padding: 0,
  flexShrink: 0,
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

/**
 * チャットに添付されたWBSアイテム1件のチップ。
 *
 * onRemove を渡すと削除ボタンが付く。入力欄の添付一覧では外せるが、
 * 送信済みバブルでは何を渡したかの記録なので外せない、という違いを表す。
 *
 * コードや実績時間などの属性は載せない。何を添付したか分かれば足り、
 * 詳細はエージェントが取得するため。判別に必要な種別だけアイコンで示す。
 */
export const WbsItemAttachmentChip = ({
  wbsItem,
  onRemove,
}: {
  wbsItem: AiAgentWbsItemRef
  onRemove?: () => void
}) => {
  const intl = useIntl()
  // 種別アイコンは WbsItemTypeVO からしか引けないため、添付が運ぶ種別文字列でストアから解決する。
  // プロジェクト未選択などで解決できないときはアイコンを出さず、名称だけ表示する。
  const wbsItemType = useSelector((state: AllState) =>
    state.project.wbsItemTypes?.get(wbsItem.type as WbsItemType)
  )

  return (
    <ChipRoot
      removable={!!onRemove}
      title={`${wbsItem.name} (${wbsItem.code})`}
    >
      {wbsItemType && <TypeIcon type={wbsItemType} />}
      <Label>{wbsItem.name}</Label>
      {onRemove && (
        <RemoveButton
          size="small"
          onClick={onRemove}
          aria-label={intl.formatMessage({
            id: 'aiAgentChat.attach.wbsItem.remove',
          })}
        >
          <CloseIcon fontSize="inherit" />
        </RemoveButton>
      )}
    </ChipRoot>
  )
}
